/** ScheduledTaskFormModal — 统一任务创建/编辑弹窗（AI 任务中心）。
 *
 * 目标类型（AI 任务 / 工作流 / 按模板周期执行 / 事项提醒）是 Segmented
 * 切换，按分支显隐字段；任务已存在时锁定（后端 PATCH 拒绝 `targetType`，
 * 契约 §6.2——改目标 = 新建任务）。频率委托 `FrequencyPicker`（持有「无
 * cron 输入」约束与首触预览；值在 antd `Form` 外自管，提交时合并）。
 *
 * AI 任务分支（吸收原 AgentRunFormModal，发起入口统一到本弹窗）：
 *  - 频率限定 单次/每天/每周/每月（once 与周期；hourly/custom_interval
 *    不对 AI 任务开放）；
 *  - 单次 → POST /agent-runs（创建即跑，后端内部补建 once 任务定义行），
 *    成功后直达 /agent-runs/:id 执行视图；
 *  - 周期 → POST /scheduled-tasks（targetType=agent_run），成功后关弹窗
 *    刷新列表；
 *  - 任务目标 once 必填、周期可选（不填按模板内置目标执行）；名称可选，
 *    留空由服务端按目标前 30 字派生；
 *  - 预算三元组（步数/时长/token 上限）在高级折叠中，once 与周期均可用。 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Collapse,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import {
  useCreateScheduledTask,
  useMatters,
  usePipelineInstances,
  usePipelineTemplates,
  useUpdateScheduledTask,
  useUsers,
} from "../../api/queries";
import { useCreateAgentRun } from "../../hooks/useAgentRuns";
import { mapApiError } from "../../utils/api-error";
import {
  SCHEDULED_TASK_INPUT_RESOLVER_LABELS,
  SCHEDULED_TASK_TARGET_TYPE_LABELS,
  type ScheduledTask,
  type ScheduledTaskCreatePayload,
  type ScheduledTaskFrequency,
  type ScheduledTaskInputResolver,
  type ScheduledTaskTargetType,
} from "../../types/scheduledTask";
import FrequencyPicker, {
  DEFAULT_FREQUENCY_VALUE,
  DEFAULT_TIMEZONE,
  type FrequencyValue,
} from "./FrequencyPicker";

const { TextArea } = Input;
const { Paragraph, Text } = Typography;

/** 波次 2：模板级目标当前唯一的输入解析器。 */
const TEMPLATE_INPUT_RESOLVER: ScheduledTaskInputResolver = "matter_documents_delta";

/** Segmented 顺序：AI 任务默认置首，其余类型沿用既有分支。 */
const TARGET_TYPE_SEGMENT_ORDER: ScheduledTaskTargetType[] = [
  "agent_run",
  "pipeline_instance",
  "pipeline_template",
  "matter_reminder",
];

/** AI 任务的频率预设（once + 周期三项；hourly/custom_interval 不开放）。 */
const AGENT_RUN_FREQUENCIES: ScheduledTaskFrequency[] = ["once", "daily", "weekly", "monthly"];

/** 非 AI 类型沿用既有全预设，但排除 once——单次仅对 AI 任务开放（后端对
 * 非 agent_run 目标 / POST /scheduled-tasks 的 once 一律拒绝，选项不可见）。 */
const NON_AGENT_RUN_FREQUENCIES: ScheduledTaskFrequency[] = [
  "hourly",
  "daily",
  "weekly",
  "monthly",
  "custom_interval",
];

/** 单次 AI 任务的频率值（无任何时间字段）。 */
const ONCE_FREQUENCY_VALUE: FrequencyValue = {
  frequency: "once",
  atHour: null,
  atMinute: null,
  byWeekday: null,
  byMonthday: null,
  intervalMinutes: null,
  timezone: DEFAULT_TIMEZONE,
};

interface ScheduledTaskFormValues {
  name: string;
  description?: string;
  targetType: ScheduledTaskTargetType;
  instanceId?: string;
  templateId?: string;
  /** agent_run 分支的执行模板（agentic 且 active；独立字段名避免与
   * pipeline_template 分支共用时切换类型残留取值）。 */
  agentTemplateId?: string;
  /** agent_run 分支的任务目标（once 必填、周期可选）。 */
  goal?: string;
  matterId?: string;
  /** 每期纳入文档上限（resolverConfig.maxDocuments）。 */
  maxDocuments?: number | null;
  reminderTitle?: string;
  reminderNote?: string;
  notifyUserIds?: string[];
  startsAt?: dayjs.Dayjs | null;
  endsAt?: dayjs.Dayjs | null;
  maxRuns?: number | null;
  /** agent_run 分支的预算三元组。 */
  maxSteps?: number | null;
  maxWallClockMinutes?: number | null;
  maxTotalTokens?: number | null;
}

/** Mirrors backend §4.4 per-frequency required fields — the picker enforces
 * the same rule client-side so the toast fires before the request round-trip.
 * `once` 没有时间字段，恒通过。 */
function validateFrequency(freq: FrequencyValue): string | null {
  switch (freq.frequency) {
    case "once":
      return null;
    case "hourly":
      return freq.atMinute == null ? "请设置每小时执行的分钟数" : null;
    case "daily":
      return freq.atHour == null || freq.atMinute == null ? "请设置每天执行的时间" : null;
    case "weekly":
      return freq.atHour == null || freq.atMinute == null || freq.byWeekday == null
        ? "请设置每周执行的星期与时间"
        : null;
    case "monthly":
      return freq.atHour == null || freq.atMinute == null || freq.byMonthday == null
        ? "请设置每月执行的日期与时间"
        : null;
    case "custom_interval":
      return freq.intervalMinutes == null || freq.intervalMinutes < 15 ? "自定义间隔至少为 15 分钟" : null;
    default:
      return null;
  }
}

/**
 * Resolve an optional-field value for the PATCH/POST payload.
 *
 * The backend reads the request body with `exclude_unset=True`
 * (`update_scheduled_task`, service.py) — a key that is *absent* means
 * "leave unchanged", while a key present with value `null` means "clear
 * it". So in edit mode, clearing a field in the form must send an explicit
 * `null`, not omit the key (which `JSON.stringify` does for `undefined`).
 * In create mode there is no prior value to preserve, so omitting an empty
 * optional field is still correct there.
 */
function clearable<T>(value: T | null | undefined, isEdit: boolean): T | null | undefined {
  return value ?? (isEdit ? null : undefined);
}

/**
 * `ScheduledTaskCreatePayload.description` (types/scheduledTask.ts) is typed
 * `string | undefined` — narrower than the backend's
 * `ScheduledTaskUpdate.description: str | None` (schemas.py:3294), which
 * does accept an explicit `null` to clear the field. The type hasn't caught
 * up with that; casting locally here rather than editing the shared type
 * file (owned by another workstream). Runtime value is what the backend
 * contract requires — the cast only affects the compile-time shape.
 */
function clearableDescription(raw: string | undefined, isEdit: boolean): string | undefined {
  const trimmed = raw?.trim();
  if (trimmed) return trimmed;
  return (isEdit ? null : undefined) as string | undefined;
}

function filterByLabel(input: string, option?: { label?: string }) {
  return (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
}

interface ScheduledTaskFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Task to edit; omit for create mode. */
  task?: ScheduledTask | null;
  onSaved?: (task: ScheduledTask) => void;
}

export default function ScheduledTaskFormModal({ open, onClose, task, onSaved }: ScheduledTaskFormModalProps) {
  const navigate = useNavigate();
  const isEdit = !!task;
  const [form] = Form.useForm<ScheduledTaskFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const createMut = useCreateScheduledTask();
  const updateMut = useUpdateScheduledTask();
  const createAgentRunMut = useCreateAgentRun();
  const targetType: ScheduledTaskTargetType = Form.useWatch("targetType", form) ?? "agent_run";
  const isAgentRun = targetType === "agent_run";

  const { data: mattersData } = useMatters({ pageSize: 100 }, { enabled: open });
  const matterOptions = (mattersData?.data ?? []).map((m) => ({ value: m.id, label: m.name }));

  const { data: instancesData } = usePipelineInstances(
    { scope: "mine" },
    { page: 1, pageSize: 100 },
  );
  const instanceOptions = (instancesData?.data ?? [])
    .filter((i) => !i.isArchived)
    .map((i) => ({ value: i.id, label: i.matterName ? `${i.name} · ${i.matterName}` : i.name }));

  // 模板级目标（波次 2）：只列 active 模板。后端 /pipeline-templates 无
  // is_active 过滤参数，前端按 isActive 过滤（真实路径读后端 router 确认，
  // 与任务底稿写的 /pipelines/templates 不同）。
  const { data: templatesData } = usePipelineTemplates({ page: 1, pageSize: 100 });
  const templateOptions = (templatesData?.data ?? [])
    .filter((t) => t.isActive)
    .map((t) => ({ value: t.id, label: t.title }));

  // AI 任务分支：仅 agentic 且 active 的模板（无 executionKind 过滤参数，
  // 按返回字段前端过滤，与 AgentRun 侧口径一致）。
  const agentTemplateOptions = (templatesData?.data ?? [])
    .filter((t) => t.isActive && t.executionKind === "agentic")
    .map((t) => ({ value: t.id, label: t.title }));

  const { data: usersData } = useUsers({ pageSize: 100 });
  const userOptions = (usersData?.data ?? []).map((u) => ({ value: u.id, label: u.name }));

  const [freq, setFreq] = useState<FrequencyValue>(ONCE_FREQUENCY_VALUE);

  useEffect(() => {
    if (!open) return;
    if (task) {
      form.setFieldsValue({
        name: task.name,
        description: task.description || undefined,
        targetType: task.targetType,
        instanceId: task.instanceId ?? undefined,
        templateId: task.templateId ?? undefined,
        agentTemplateId: task.templateId ?? undefined,
        goal: task.goal ?? undefined,
        matterId: task.matterId ?? undefined,
        maxDocuments: task.resolverConfig?.maxDocuments ?? undefined,
        reminderTitle: task.reminderTitle ?? undefined,
        reminderNote: task.reminderNote ?? undefined,
        notifyUserIds: task.notifyUserIds ?? [],
        startsAt: task.startsAt ? dayjs(task.startsAt) : null,
        endsAt: task.endsAt ? dayjs(task.endsAt) : null,
        maxRuns: task.maxRuns ?? undefined,
        maxSteps: task.maxSteps ?? undefined,
        maxWallClockMinutes: task.maxWallClockMinutes ?? undefined,
        maxTotalTokens: task.maxTotalTokens ?? undefined,
      });
      setFreq({
        frequency: task.frequency,
        atHour: task.atHour,
        atMinute: task.atMinute,
        byWeekday: task.byWeekday,
        byMonthday: task.byMonthday,
        intervalMinutes: task.intervalMinutes,
        timezone: task.timezone,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ targetType: "agent_run" });
      setFreq(ONCE_FREQUENCY_VALUE);
    }
  }, [open, task, form]);

  // 类型切换时收敛频率白名单：AI 任务限定四项（越界值回落单次），非 AI
  // 类型不出现 once（回落默认每天）。
  useEffect(() => {
    if (!open) return;
    if (isAgentRun && !AGENT_RUN_FREQUENCIES.includes(freq.frequency)) {
      setFreq(ONCE_FREQUENCY_VALUE);
    } else if (!isAgentRun && freq.frequency === "once") {
      setFreq(DEFAULT_FREQUENCY_VALUE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetType, open]);

  /** 预算三元组的提交形状：留空不进 payload（走后端默认/硬顶口径）。 */
  const budgetPayload = (values: ScheduledTaskFormValues) => ({
    ...(values.maxSteps != null ? { maxSteps: values.maxSteps } : {}),
    ...(values.maxWallClockMinutes != null ? { maxWallClockMinutes: values.maxWallClockMinutes } : {}),
    ...(values.maxTotalTokens != null ? { maxTotalTokens: values.maxTotalTokens } : {}),
  });

  /** AI 任务分支提交：once → POST /agent-runs（创建即跑，直达执行视图）；
   * 周期 → POST /scheduled-tasks（targetType=agent_run）。 */
  const handleSubmitAgentRun = async (values: ScheduledTaskFormValues): Promise<boolean> => {
    const goal = values.goal?.trim() ?? "";

    if (!values.agentTemplateId) {
      messageApi.warning("请选择执行模板");
      return false;
    }
    if (!values.matterId) {
      messageApi.warning("请选择所属事项");
      return false;
    }
    if (freq.frequency === "once" && !goal) {
      messageApi.warning("请描述任务目标");
      return false;
    }
    const freqError = validateFrequency(freq);
    if (freqError) {
      messageApi.warning(freqError);
      return false;
    }

    if (freq.frequency === "once") {
      try {
        const run = await createAgentRunMut.mutateAsync({
          goal,
          templateId: values.agentTemplateId,
          matterId: values.matterId,
          ...(values.name?.trim() ? { name: values.name.trim() } : {}),
          ...budgetPayload(values),
        });
        messageApi.success("任务已发起，正在执行");
        onClose();
        navigate(`/agent-runs/${run.id}`);
        return true;
      } catch (err) {
        messageApi.error(mapApiError(err));
        return false;
      }
    }

    try {
      const payload: ScheduledTaskCreatePayload = {
        name: values.name?.trim() ?? "",
        targetType: "agent_run",
        templateId: values.agentTemplateId,
        matterId: values.matterId,
        ...(goal ? { goal } : {}),
        ...budgetPayload(values),
        frequency: freq.frequency,
        atHour: freq.atHour,
        atMinute: freq.atMinute,
        byWeekday: freq.byWeekday,
        byMonthday: freq.byMonthday,
        intervalMinutes: freq.intervalMinutes,
        timezone: freq.timezone,
        startsAt: clearable(values.startsAt ? values.startsAt.toISOString() : null, isEdit),
        endsAt: clearable(values.endsAt ? values.endsAt.toISOString() : null, isEdit),
        maxRuns: clearable(values.maxRuns, isEdit),
      };
      const saved = await createMut.mutateAsync(payload);
      messageApi.success("任务已创建");
      onSaved?.(saved);
      onClose();
      return true;
    } catch (err) {
      messageApi.error(mapApiError(err));
      return false;
    }
  };

  const handleSubmit = async () => {
    let values: ScheduledTaskFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    if (values.targetType === "agent_run" && !isEdit) {
      await handleSubmitAgentRun(values);
      return;
    }

    const freqError = validateFrequency(freq);
    if (freqError) {
      messageApi.warning(freqError);
      return;
    }
    if (values.targetType === "pipeline_instance" && !values.instanceId) {
      messageApi.warning("请选择要执行的工作流实例");
      return;
    }
    if (values.targetType === "matter_reminder" && (!values.matterId || !values.reminderTitle?.trim())) {
      messageApi.warning("请选择所属事项并填写提醒标题");
      return;
    }
    if (values.targetType === "pipeline_template" && (!values.templateId || !values.matterId)) {
      messageApi.warning("请选择工作流模板与所属事项");
      return;
    }

    const basePayload = {
      name: values.name.trim(),
      description: clearableDescription(values.description, isEdit),
      instanceId: values.targetType === "pipeline_instance" ? values.instanceId : undefined,
      templateId:
        values.targetType === "pipeline_template"
          ? values.templateId
          : values.targetType === "agent_run"
            ? values.agentTemplateId
            : undefined,
      matterId:
        values.targetType === "matter_reminder" ||
        values.targetType === "pipeline_template" ||
        values.targetType === "agent_run"
          ? values.matterId
          : undefined,
      inputResolver:
        values.targetType === "pipeline_template" ? TEMPLATE_INPUT_RESOLVER : undefined,
      // 编辑时保留表单未接管的 firstRunWindowDays（后端 exclude_unset 语义下
      // 缺省即"不变"），仅覆盖 maxDocuments 一项。
      resolverConfig:
        values.targetType === "pipeline_template"
          ? {
              maxDocuments: clearable(values.maxDocuments, isEdit) ?? null,
              ...(isEdit && task?.resolverConfig?.firstRunWindowDays != null
                ? { firstRunWindowDays: task.resolverConfig.firstRunWindowDays }
                : {}),
            }
          : undefined,
      reminderTitle: values.targetType === "matter_reminder" ? values.reminderTitle?.trim() : undefined,
      reminderNote:
        values.targetType === "matter_reminder"
          ? clearable(values.reminderNote?.trim() || null, isEdit)
          : undefined,
      notifyUserIds: values.targetType === "matter_reminder" ? values.notifyUserIds : undefined,
      // 注：goal 与预算三元组只在创建路径提交（AI 任务创建见
      // handleSubmitAgentRun；ScheduledTaskUpdate 不含这两组字段，once 任务
      // 后端禁 PATCH）。
      frequency: freq.frequency,
      atHour: freq.atHour,
      atMinute: freq.atMinute,
      byWeekday: freq.byWeekday,
      byMonthday: freq.byMonthday,
      intervalMinutes: freq.intervalMinutes,
      timezone: freq.timezone,
      startsAt: clearable(values.startsAt ? values.startsAt.toISOString() : null, isEdit),
      endsAt: clearable(values.endsAt ? values.endsAt.toISOString() : null, isEdit),
      maxRuns: clearable(values.maxRuns, isEdit),
    };

    try {
      if (isEdit && task) {
        const saved = await updateMut.mutateAsync({ id: task.id, data: basePayload });
        messageApi.success("任务已更新");
        onSaved?.(saved);
      } else {
        const payload: ScheduledTaskCreatePayload = { ...basePayload, targetType: values.targetType };
        const saved = await createMut.mutateAsync(payload);
        messageApi.success("任务已创建");
        onSaved?.(saved);
      }
      onClose();
    } catch (err) {
      messageApi.error(mapApiError(err));
    }
  };

  const submitPending =
    createMut.isPending || updateMut.isPending || createAgentRunMut.isPending;

  const handleClose = () => {
    if (submitPending) return;
    onClose();
  };

  const isOnce = freq.frequency === "once";

  return (
    <Modal
      title={isEdit ? "编辑任务" : "新建任务"}
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      confirmLoading={submitPending}
      okText={isEdit ? "保存" : "创建"}
      cancelText="取消"
      width={640}
      destroyOnHidden
      rootClassName="jerryTaskFormModal"
      data-testid="scheduled-task-form-modal"
    >
      {contextHolder}
      <Form form={form} layout="vertical" initialValues={{ targetType: "agent_run" }}>
        <Form.Item
          name="name"
          label="任务名称"
          rules={
            isEdit || !isAgentRun
              ? [{ required: true, message: "请输入任务名称" }]
              : []
          }
          extra={!isEdit && isAgentRun ? "可选；留空自动取任务目标前 30 字" : undefined}
        >
          <Input
            placeholder={isAgentRun ? "例如：合同风险梳理" : "例如：每周合同风险复查"}
            maxLength={200}
            data-testid="scheduled-task-name-input"
          />
        </Form.Item>
        {!isAgentRun && (
          <Form.Item name="description" label="说明">
            <TextArea rows={2} maxLength={500} placeholder="可选说明" />
          </Form.Item>
        )}

        <Form.Item name="targetType" label="任务类型" data-testid="scheduled-task-type-field">
          <Segmented
            block
            disabled={isEdit}
            options={TARGET_TYPE_SEGMENT_ORDER.map((value) => ({
              value,
              label: SCHEDULED_TASK_TARGET_TYPE_LABELS[value],
            }))}
          />
        </Form.Item>

        {isAgentRun ? (
          <>
            <Form.Item
              name="goal"
              label="任务目标"
              rules={!isEdit && isOnce ? [{ required: true, message: "请描述任务目标" }] : []}
              extra={
                isEdit
                  ? "任务目标创建后暂不支持修改"
                  : isOnce
                    ? "描述要让任务完成的目标，创建后立即执行一次"
                    : "可选；不填则每期按模板内置目标执行"
              }
            >
              <TextArea
                rows={3}
                maxLength={2000}
                disabled={isEdit}
                placeholder="例如：梳理本事项全部合同的风险条款，输出风险清单与整改建议"
                data-testid="agent-run-goal-input"
              />
            </Form.Item>
            <Form.Item
              name="agentTemplateId"
              label="执行模板"
              rules={[{ required: true, message: "请选择执行模板" }]}
            >
              <Select
                showSearch
                placeholder="选择要执行的 AI 任务模板"
                options={agentTemplateOptions}
                filterOption={filterByLabel}
                data-testid="agent-run-template-select"
              />
            </Form.Item>
            <Form.Item
              name="matterId"
              label="所属事项"
              rules={[{ required: true, message: "请选择所属事项" }]}
            >
              <Select
                showSearch
                placeholder="选择事项"
                options={matterOptions}
                filterOption={filterByLabel}
                data-testid="agent-run-matter-select"
              />
            </Form.Item>
          </>
        ) : targetType === "pipeline_instance" ? (
          <Form.Item
            name="instanceId"
            label="工作流实例"
            rules={[{ required: true, message: "请选择工作流实例" }]}
          >
            <Select
              showSearch
              placeholder="选择要按周期触发的工作流实例"
              options={instanceOptions}
              filterOption={filterByLabel}
            />
          </Form.Item>
        ) : targetType === "pipeline_template" ? (
          <>
            <Form.Item
              name="templateId"
              label="工作流模板"
              rules={[{ required: true, message: "请选择工作流模板" }]}
            >
              <Select
                showSearch
                placeholder="选择要周期执行的模板"
                options={templateOptions}
                filterOption={filterByLabel}
                data-testid="scheduled-task-template-select"
              />
            </Form.Item>
            <Form.Item
              name="matterId"
              label="所属事项"
              rules={[{ required: true, message: "请选择所属事项" }]}
            >
              <Select showSearch placeholder="选择事项" options={matterOptions} filterOption={filterByLabel} />
            </Form.Item>
            <Form.Item
              name="maxDocuments"
              label="每期纳入文档上限"
              extra={`留空使用默认上限。解析器：${SCHEDULED_TASK_INPUT_RESOLVER_LABELS[TEMPLATE_INPUT_RESOLVER]}`}
            >
              <InputNumber
                min={1}
                max={200}
                precision={0}
                style={{ width: 140 }}
                placeholder="不限"
                data-testid="scheduled-task-max-documents"
              />
            </Form.Item>
            <Paragraph type="secondary" style={{ marginTop: 0, marginBottom: "var(--jerry-sp-2)" }}>
              <Text type="secondary">
                每期自动纳入该事项自上次运行以来新上传的文档；首期回看创建任务前的一段窗口。
              </Text>
            </Paragraph>
          </>
        ) : (
          <>
            <Form.Item name="matterId" label="所属事项" rules={[{ required: true, message: "请选择所属事项" }]}>
              <Select showSearch placeholder="选择事项" options={matterOptions} filterOption={filterByLabel} />
            </Form.Item>
            <Form.Item
              name="reminderTitle"
              label="提醒标题"
              rules={[{ required: true, message: "请输入提醒标题" }]}
            >
              <Input placeholder="例如：合同续签窗口临近" maxLength={200} />
            </Form.Item>
            <Form.Item name="reminderNote" label="提醒内容">
              <TextArea rows={3} maxLength={2000} placeholder="到点后随通知一并发送的说明" />
            </Form.Item>
            <Form.Item name="notifyUserIds" label="通知接收人">
              <Select
                mode="multiple"
                allowClear
                placeholder="默认通知创建人"
                options={userOptions}
                maxTagCount="responsive"
                filterOption={filterByLabel}
              />
            </Form.Item>
          </>
        )}

        <Form.Item label="执行频率" required>
          <FrequencyPicker
            value={freq}
            onChange={setFreq}
            frequencies={
              isAgentRun
                ? // 编辑态排除「单次」：once 不可由周期任务切换（后端 PATCH
                  // 拒绝 frequency=once），创建态才提供四项。
                  isEdit
                  ? AGENT_RUN_FREQUENCIES.filter((f) => f !== "once")
                  : AGENT_RUN_FREQUENCIES
                : NON_AGENT_RUN_FREQUENCIES
            }
          />
        </Form.Item>

        <Collapse
          ghost
          items={[
            {
              key: "advanced",
              label: "高级选项（可选）",
              children: (
                <Space size="middle" wrap>
                  {isAgentRun && (
                    <>
                      <Form.Item name="maxSteps" label="步数上限" style={{ marginBottom: 0 }}>
                        <InputNumber
                          min={1}
                          precision={0}
                          style={{ width: 140 }}
                          placeholder="不限"
                          disabled={isEdit}
                          data-testid="agent-run-max-steps"
                        />
                      </Form.Item>
                      <Form.Item name="maxWallClockMinutes" label="时长上限（分钟）" style={{ marginBottom: 0 }}>
                        <InputNumber
                          min={1}
                          precision={0}
                          style={{ width: 140 }}
                          placeholder="不限"
                          disabled={isEdit}
                          data-testid="agent-run-max-minutes"
                        />
                      </Form.Item>
                      <Form.Item name="maxTotalTokens" label="token 上限" style={{ marginBottom: 0 }}>
                        <InputNumber
                          min={1}
                          precision={0}
                          style={{ width: 140 }}
                          placeholder="不限"
                          disabled={isEdit}
                          data-testid="agent-run-max-tokens"
                        />
                      </Form.Item>
                    </>
                  )}
                  {/* 生效窗口与次数上限仅对周期任务有意义（once 创建即触发、
                      不重臂）。 */}
                  {!isAgentRun || !isOnce ? (
                    <>
                      <Form.Item name="startsAt" label="生效开始时间" style={{ marginBottom: 0 }}>
                        <DatePicker showTime style={{ width: 200 }} />
                      </Form.Item>
                      <Form.Item name="endsAt" label="生效结束时间" style={{ marginBottom: 0 }}>
                        <DatePicker showTime style={{ width: 200 }} />
                      </Form.Item>
                      <Form.Item name="maxRuns" label="最多执行次数" style={{ marginBottom: 0 }}>
                        <InputNumber min={1} style={{ width: 140 }} placeholder="不限" />
                      </Form.Item>
                    </>
                  ) : null}
                </Space>
              ),
            },
          ]}
        />
      </Form>
    </Modal>
  );
}
